// Budget de calcul du suivi de visage (effet Avatar uniquement — c'est le seul effet à
// demander `needsFaceTracking`).
//
// LE DÉFAUT. detectFaceInVideo() était appelée à CHAQUE image de la boucle de rendu, sur
// une image 1080p, et `FaceLandmarker.detectForVideo` est SYNCHRONE : tout son temps de
// calcul bloque le thread principal. Le 25/10/2026 (Ales-TV, Windows/Chrome), le thread
// principal était déjà à ~90 % ; avec l'Avatar actif le programme tombait de 13 à 3-4 i/s
// (pendant ces creux, le travail média passait de ~200 à ~42 ms/s : les threads de
// décodage et d'encodage attendaient le thread principal), figeant mini-vue ET final.
//
// DEUX LEVIERS, qui ne changent ni le nombre de visages, ni les expressions, ni le modèle :
//   - une cadence plafonnée (le suivi n'a pas besoin de 25 Hz : le rendu de l'avatar lisse
//     déjà ses valeurs, et entre deux détections on réutilise la dernière) ;
//   - une image réduite (les repères sont NORMALISÉS 0..1 : la réduction ne les décale pas).
// Fonctions pures, hors du module MediaPipe, pour être testées sans WASM ni vidéo.

/** Intervalle minimal entre deux détections (≈ 12,5 Hz). */
export const FACE_DETECT_MIN_INTERVAL_MS = 80;

/** Plus grand côté de l'image donnée au détecteur. 640 px garde la finesse utile aux
 *  paupières et à la bouche ; le 1080p n'apportait que du coût. */
export const FACE_DETECT_MAX_SIDE_PX = 640;

/** Vrai si une nouvelle détection est due. `lastAt` est null avant la première. */
export function isFaceDetectionDue(now: number, lastAt: number | null, minIntervalMs = FACE_DETECT_MIN_INTERVAL_MS): boolean {
  return lastAt === null || now - lastAt >= minIntervalMs || now < lastAt;
}

/** Dimensions de l'image réduite : même proportion, jamais agrandie, au moins 1 px. */
export function fitWithin(width: number, height: number, maxSide = FACE_DETECT_MAX_SIDE_PX): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxSide || longest <= 0) return { width: Math.max(1, width), height: Math.max(1, height) };
  const k = maxSide / longest;
  return { width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)) };
}

/** Part de la résolution à laquelle on floute la personne cachée (cf. blurPersonCutout). */
export const HIDE_BODY_BLUR_SCALE = 4;

/** Taille de travail pour un flou à résolution réduite : `factor` fois plus petite, au moins 1 px. */
export function reducedSize(width: number, height: number, factor = HIDE_BODY_BLUR_SCALE): { width: number; height: number } {
  return { width: Math.max(1, Math.round(width / factor)), height: Math.max(1, Math.round(height / factor)) };
}

/**
 * Effets dont le détourage est plafonné en cadence (Avatar et Arrière-plan). Maquillage, qui
 * partage la même segmentation, garde son rythme d'origine : le plafond ne s'applique que si
 * TOUS les effets qui demandent le détourage sont dans cette liste.
 */
export const SEGMENTATION_CAPPED_EFFECT_IDS: readonly string[] = ['avatar', 'surroundings'];

export function isSegmentationCappable(requestingEffectIds: readonly string[]): boolean {
  return requestingEffectIds.length > 0 && requestingEffectIds.every(id => SEGMENTATION_CAPPED_EFFECT_IDS.includes(id));
}
